/**
 * Positions the search modal below the header search field (width match).
 *
 * While open, the dialog-component is moved to document.body so `position: fixed` matches
 * viewport `getBoundingClientRect()` (no transformed/sticky ancestor mismatch).
 */
const GAP_PX = 6;
const VIEWPORT_PADDING_PX = 12;
/** Viewports at or below this width use a full-width search modal (mobile + tablet). */
const FULL_WIDTH_MODAL_MAX_PX = 1024;

/** @type {WeakMap<HTMLElement, { placeholder: Comment; parent: HTMLElement }>} */
const searchModalPortalState = new WeakMap();

/**
 * @param {HTMLElement} modal - dialog-component.search-modal
 */
function moveSearchModalToBody(modal) {
  if (modal.parentElement === document.body) return;
  const parent = modal.parentElement;
  if (!parent) return;
  const placeholder = document.createComment('search-modal-placeholder');
  parent.insertBefore(placeholder, modal);
  document.body.appendChild(modal);
  searchModalPortalState.set(modal, { placeholder, parent });
}

/**
 * @param {HTMLElement} modal
 */
function restoreSearchModalToSlot(modal) {
  const state = searchModalPortalState.get(modal);
  if (!state) return;
  const { placeholder, parent } = state;
  if (!parent.parentNode) {
    searchModalPortalState.delete(modal);
    return;
  }
  parent.insertBefore(modal, placeholder);
  placeholder.remove();
  searchModalPortalState.delete(modal);
}

/**
 * @param {HTMLElement} modal
 * @returns {{ input: HTMLInputElement; bar: HTMLElement } | null}
 */
function getSearchAnchorElements(modal) {
  const bar = document.querySelector(
    '#header-component .header__search-slot [data-search-modal-anchor]',
  );
  if (bar instanceof HTMLElement) {
    const input = bar.querySelector('#header-search-input');
    if (input instanceof HTMLInputElement) return { input, bar };
  }

  const actionContainer = modal.closest('.search-action-container.search-action');
  const searchHost = actionContainer?.querySelector(':scope > search-button');
  const fallbackInput = searchHost?.querySelector('input.search-input:not([type="hidden"])');
  if (fallbackInput instanceof HTMLInputElement) {
    const fallbackBar =
      fallbackInput.closest('[data-search-modal-anchor]') ??
      fallbackInput.closest('.predictive-search-form__header') ??
      (searchHost instanceof HTMLElement ? searchHost : actionContainer);
    if (fallbackBar instanceof HTMLElement) return { input: fallbackInput, bar: fallbackBar };
  }

  return null;
}

/**
 * @param {HTMLElement} modal
 */
function positionSearchModal(modal) {
  const dialogEl = modal.querySelector('dialog');
  const backdropEl = modal.querySelector('.search-modal__backdrop');
  if (!dialogEl?.open) return;

  const anchor = getSearchAnchorElements(modal);
  if (!anchor) return;

  const { input, bar } = anchor;
  const inputRect = input.getBoundingClientRect();
  const barRect = bar.getBoundingClientRect();

  let topPx = inputRect.bottom + GAP_PX;
  if (inputRect.width === 0 && inputRect.height === 0 && barRect.height > 0) {
    topPx = barRect.bottom + GAP_PX;
  }

  const anchorRect = inputRect.width > 0 ? inputRect : barRect;
  const useFullWidthModal = window.innerWidth <= FULL_WIDTH_MODAL_MAX_PX;
  const width = useFullWidthModal ? window.innerWidth : anchorRect.width;
  const leftPx = useFullWidthModal
    ? 0
    : Math.min(
        Math.max(anchorRect.left, VIEWPORT_PADDING_PX),
        Math.max(VIEWPORT_PADDING_PX, window.innerWidth - width - VIEWPORT_PADDING_PX),
      );

  const maxHeight = Math.max(
    200,
    window.innerHeight - inputRect.bottom - GAP_PX - VIEWPORT_PADDING_PX,
  );

  dialogEl.style.setProperty('--search-modal-top', `${topPx}px`);
  dialogEl.style.setProperty('--search-modal-left', `${leftPx}px`);
  dialogEl.style.setProperty('--search-modal-width', `${width}px`);
  dialogEl.style.setProperty('--search-modal-max-height', `${maxHeight}px`);

  dialogEl.style.setProperty('position', 'fixed');
  dialogEl.style.setProperty('inset', 'unset');
  dialogEl.style.setProperty('top', `${topPx}px`);
  dialogEl.style.setProperty('left', `${leftPx}px`);
  dialogEl.style.setProperty('right', 'auto');
  dialogEl.style.setProperty('bottom', 'auto');
  dialogEl.style.setProperty('width', `${width}px`);
  dialogEl.style.setProperty('max-height', `${maxHeight}px`);
  dialogEl.style.setProperty('margin', '0');
  dialogEl.style.setProperty('z-index', 'var(--layer-overlay)');

  const headerEl = document.getElementById('header-component');
  const backdropTopPx = headerEl ? headerEl.getBoundingClientRect().bottom : topPx;
  modal.style.setProperty('--search-modal-backdrop-inset-top', `${backdropTopPx}px`);
  if (backdropEl instanceof HTMLElement) {
    backdropEl.style.top = `${backdropTopPx}px`;
  }
}

/**
 * @param {HTMLElement} modal
 */
function positionSearchModalNextFrames(modal) {
  const run = () => positionSearchModal(modal);
  run();
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      run();
      requestAnimationFrame(run);
    });
  });
}

/**
 * @param {HTMLElement} modal
 */
function setupSearchModalAnchor(modal) {
  /** @type {ResizeObserver | null} */
  let headerComponentObserver = null;
  /** @type {MutationObserver | null} */
  let stickyStateObserver = null;

  const runIfOpen = () => {
    if (modal.querySelector('dialog')?.open) positionSearchModalNextFrames(modal);
  };

  const onScrollOrResize = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (modal.querySelector('dialog')?.open) positionSearchModal(modal);
      });
    });
  };

  /**
   * Backdrop starts below the header, so header clicks never hit it.
   * Close search when the user clicks header chrome outside the search field.
   * @param {MouseEvent | PointerEvent} event
   */
  const onHeaderOutsideClick = (event) => {
    const dialog = modal.querySelector('dialog');
    if (!dialog?.open) return;

    const target = event.target;
    if (!(target instanceof Node)) return;

    /* Keep open when interacting with the dropdown itself */
    if (modal.contains(target)) return;

    const headerEl = document.getElementById('header-component');
    if (!headerEl || !headerEl.contains(target)) return;

    const anchor = getSearchAnchorElements(modal);
    if (anchor?.bar?.contains(target)) return;

    const ps = document.getElementById('search-modal-predictive-component');
    if (ps && typeof /** @type {{ closeSearchAndDialog?: () => void }} */ (ps).closeSearchAndDialog === 'function') {
      /** @type {{ closeSearchAndDialog: () => void }} */ (ps).closeSearchAndDialog();
      return;
    }

    const host = /** @type {HTMLElement & { closeDialog?: () => void }} */ (modal);
    host.closeDialog?.();
  };

  modal.addEventListener('dialog:open', () => {
    moveSearchModalToBody(modal);
    modal.setAttribute('data-open', 'true');
    document.querySelector('#header-component .header__search-slot')?.setAttribute('data-search-open', 'true');
    positionSearchModalNextFrames(modal);

    const headerComponent = document.getElementById('header-component');

    if (headerComponent && typeof ResizeObserver !== 'undefined') {
      headerComponentObserver = new ResizeObserver(runIfOpen);
      headerComponentObserver.observe(headerComponent);
    }

    if (headerComponent && typeof MutationObserver !== 'undefined') {
      stickyStateObserver = new MutationObserver(runIfOpen);
      stickyStateObserver.observe(headerComponent, {
        attributes: true,
        attributeFilter: ['data-sticky-state', 'data-scroll-direction'],
      });
    }

    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('scrollend', runIfOpen, { passive: true });
    /* Capture so we close even if a header control stops propagation */
    document.addEventListener('click', onHeaderOutsideClick, true);
  });

  modal.addEventListener('dialog:close', () => {
    modal.setAttribute('data-open', 'false');
    document.querySelector('#header-component .header__search-slot')?.setAttribute('data-search-open', 'false');
    headerComponentObserver?.disconnect();
    headerComponentObserver = null;
    stickyStateObserver?.disconnect();
    stickyStateObserver = null;
    window.removeEventListener('resize', onScrollOrResize);
    window.removeEventListener('scroll', onScrollOrResize, true);
    window.removeEventListener('scrollend', runIfOpen);
    document.removeEventListener('click', onHeaderOutsideClick, true);
    restoreSearchModalToSlot(modal);
  });
}

document.querySelectorAll('dialog-component.search-modal').forEach((el) => {
  if (el instanceof HTMLElement) setupSearchModalAnchor(el);
});

/**
 * Header search is outside `predictive-search-component`, so `on:input="/search"` does not bind there.
 * Indirect flow: mirror header value into the modal input and dispatch `InputEvent` so the same `/search` + fetch runs.
 */
let indirectHeaderPredictiveWired = false;

function getPrimaryHeaderSearchInput() {
  const fromSlot = document.querySelector(
    '#header-component .header__search-slot [data-search-modal-anchor] input.search-input:not([type="hidden"])',
  );
  if (fromSlot instanceof HTMLInputElement) return fromSlot;
  return null;
}

function getModalPredictiveSearchInput() {
  const ps = document.querySelector('#search-modal predictive-search-component');
  const input = ps?.querySelector('input.search-input:not([type="hidden"])');
  return input instanceof HTMLInputElement ? input : null;
}

function openSearchModalIfNeeded() {
  const modal = document.getElementById('search-modal');
  const host = /** @type {HTMLElement & { showDialog?: () => void }} */ (modal);
  if (!host) return;
  const dlg = modal.querySelector('dialog');
  if (dlg && !dlg.open && typeof host.showDialog === 'function') host.showDialog();
}

function wireIndirectHeaderPredictiveSearch() {
  if (indirectHeaderPredictiveWired) return;

  const headerInput = getPrimaryHeaderSearchInput();
  const modalInput = getModalPredictiveSearchInput();
  const ps = document.querySelector('#search-modal predictive-search-component');

  if (!(headerInput instanceof HTMLInputElement) || !(modalInput instanceof HTMLInputElement) || !(ps instanceof HTMLElement)) {
    return;
  }

  if (headerInput === modalInput) {
    indirectHeaderPredictiveWired = true;
    return;
  }

  indirectHeaderPredictiveWired = true;

  const component = /** @type {HTMLElement & {
    onSearchKeyDown?: (e: KeyboardEvent) => void;
    resetSearch?: () => void;
    loadEmptyStateIfNeeded?: () => void;
  }} */ (ps);

  const requestEmptyStateAfterEngage = () => {
    component.loadEmptyStateIfNeeded?.();
  };

  const unlockHeaderInput = () => {
    if (headerInput.hasAttribute('readonly')) {
      headerInput.removeAttribute('readonly');
      headerInput.removeAttribute('data-autofill-guard');
    }
  };

  /** Keep caret visible in the header input after click/open timing. */
  const focusHeaderInputForTyping = () => {
    unlockHeaderInput();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (document.activeElement !== headerInput) {
          headerInput.focus({ preventScroll: true });
        }
        const pos = headerInput.value.length;
        try {
          headerInput.setSelectionRange(pos, pos);
        } catch {
          /* selection API may fail for unsupported input modes */
        }
      });
    });
  };

  headerInput.addEventListener('pointerdown', requestEmptyStateAfterEngage);
  headerInput.addEventListener('pointerdown', focusHeaderInputForTyping);
  headerInput.addEventListener('click', requestEmptyStateAfterEngage);
  headerInput.addEventListener('click', focusHeaderInputForTyping);
  headerInput.addEventListener('focus', () => {
    unlockHeaderInput();
    requestEmptyStateAfterEngage();
    if (headerInput.value.length > 0) {
      modalInput.value = headerInput.value;
      /* Do not open the modal here — bar click / input already handles open (avoids double open). */
    }
  });

  headerInput.addEventListener('input', (e) => {
    modalInput.value = headerInput.value;
    openSearchModalIfNeeded();
    const inputType = e instanceof InputEvent && e.inputType ? e.inputType : 'insertText';
    modalInput.dispatchEvent(new InputEvent('input', { bubbles: true, cancelable: true, inputType }));
  });

  const submitHeaderSearch = (event) => {
    modalInput.value = headerInput.value;
    if (typeof component.onSearchKeyDown !== 'function') return false;
    component.onSearchKeyDown(event);
    return event.defaultPrevented;
  };

  headerInput.addEventListener('keydown', (e) => {
    unlockHeaderInput();
    modalInput.value = headerInput.value;
    if (typeof component.onSearchKeyDown === 'function') {
      component.onSearchKeyDown(/** @type {KeyboardEvent} */ (e));
    }
    if (e.defaultPrevented) return;
    openSearchModalIfNeeded();
  });

  /* Mobile keyboards fire `search` instead of Enter when the field is type="search". */
  headerInput.addEventListener('search', () => {
    const query = headerInput.value.trim();
    if (!query) return;
    const enterEvent = new KeyboardEvent('keydown', { key: 'Enter', cancelable: true });
    if (!submitHeaderSearch(enterEvent)) {
      openSearchModalIfNeeded();
    }
  });

  const bar = headerInput.closest('[data-search-modal-anchor]');

  /* Search icon click/tap submits like Enter (keyword redirect, single result, or results page). */
  const searchIcon = bar?.querySelector('.predictive-search__icon');
  if (searchIcon instanceof HTMLElement) {
    searchIcon.style.cursor = 'pointer';
    searchIcon.addEventListener('click', (e) => {
      const query = headerInput.value.trim();
      if (!query) return;
      e.preventDefault();
      e.stopPropagation();
      modalInput.value = headerInput.value;
      const enterEvent = new KeyboardEvent('keydown', { key: 'Enter', cancelable: true });
      if (typeof component.onSearchKeyDown === 'function') {
        component.onSearchKeyDown(enterEvent);
      } else {
        const root = /** @type {{ routes?: { root?: string } }} */ (window.Shopify)?.routes?.root ?? '/';
        window.location.href = `${root}search?q=${encodeURIComponent(query)}&options%5Bprefix%5D=last`;
      }
    });
  }

  const resetBtn = bar?.querySelector('button.predictive-search__reset-button');
  if (resetBtn instanceof HTMLButtonElement) {
    resetBtn.addEventListener('click', (e) => {
      e.preventDefault();
      if (typeof component.resetSearch === 'function') {
        component.resetSearch();
      }
      headerInput.value = '';
    });
  }

  const modalHost = document.getElementById('search-modal');
  if (modalHost) {
    modalHost.addEventListener('dialog:open', requestEmptyStateAfterEngage);
    modalHost.addEventListener('dialog:close', () => {
      queueMicrotask(() => {
        headerInput.value = modalInput.value;
      });
    });
  }
}

function scheduleIndirectHeaderWire() {
  wireIndirectHeaderPredictiveSearch();
  if (indirectHeaderPredictiveWired) return;
  [32, 120, 400].forEach((ms) => setTimeout(wireIndirectHeaderPredictiveSearch, ms));
}

/**
 * Rotating typewriter-style placeholder for the header search input (empty field only).
 * Reads JSON from #header-search-typewriter-config { base, raw } — raw uses ## between groups, # within a group (Dawn-style).
 */
function initHeaderSearchTypewriterPlaceholder() {
  const configEl = document.getElementById('header-search-typewriter-config');
  if (!configEl?.textContent?.trim()) return;

  /** @type {{ base?: unknown; raw?: unknown }} */
  let config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch {
    return;
  }

  const raw = typeof config.raw === 'string' ? config.raw : '';
  if (!raw.trim()) return;

  const basePlaceholder = typeof config.base === 'string' && config.base.trim() ? config.base.trim() : 'Search';

  const collectionNames = raw
    .split('##')
    .map((group) => group.split('#').map((s) => s.trim()).filter(Boolean))
    .flat();

  if (collectionNames.length === 0) return;

  const inputField = document.querySelector(
    '#header-component .header__search-slot [data-search-modal-anchor] input.search-input:not([type="hidden"])',
  );

  if (!(inputField instanceof HTMLInputElement)) return;
  const headerSearchInput = inputField;

  let currentIndex = 0;
  let charIndex = 0;
  /** @type {ReturnType<typeof setInterval> | null} */
  let typingInterval = null;

  function stopTyping() {
    if (typingInterval != null) {
      clearInterval(typingInterval);
      typingInterval = null;
    }
  }

  function shouldAnimate() {
    return headerSearchInput.value.trim() === '';
  }

  function typeCollectionName() {
    if (!shouldAnimate()) {
      stopTyping();
      return;
    }

    const currentName = collectionNames[currentIndex] ?? collectionNames[0] ?? '';
    const newPlaceholder = `${basePlaceholder} ${currentName.slice(0, charIndex + 1)}`;
    headerSearchInput.setAttribute('placeholder', newPlaceholder);
    charIndex++;

    if (charIndex === currentName.length) {
      stopTyping();
      setTimeout(() => {
        charIndex = 0;
        currentIndex = (currentIndex + 1) % collectionNames.length;
        if (shouldAnimate()) {
          typingInterval = setInterval(typeCollectionName, 100);
        }
      }, 2000);
    }
  }

  function startTyping() {
    stopTyping();
    if (!shouldAnimate()) return;
    charIndex = 0;
    typingInterval = setInterval(typeCollectionName, 100);
  }

  headerSearchInput.addEventListener('focus', () => {
    if (headerSearchInput.value.trim() === '') {
      charIndex = 0;
      startTyping();
    } else {
      stopTyping();
      headerSearchInput.setAttribute('placeholder', basePlaceholder);
    }
  });

  headerSearchInput.addEventListener('blur', () => {
    if (headerSearchInput.value.trim() === '') {
      charIndex = 0;
      startTyping();
    }
  });

  headerSearchInput.addEventListener('input', () => {
    if (headerSearchInput.value.trim() !== '') {
      stopTyping();
      headerSearchInput.setAttribute('placeholder', basePlaceholder);
    } else {
      charIndex = 0;
      startTyping();
    }
  });

  startTyping();
}

function bootSearchModalAnchor() {
  scheduleIndirectHeaderWire();
  initHeaderSearchTypewriterPlaceholder();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootSearchModalAnchor);
} else {
  bootSearchModalAnchor();
}
